import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { jsPDF } from "jspdf";
import { Key, ArrowRight, ArrowLeft, Download, Shield, Lightbulb, CheckCircle, TrendingUp, Calendar, PoundSterling, Home, AlertTriangle, Calculator, FileText, CreditCard, Share2, X, Mail, ThumbsUp, ThumbsDown, Send } from "lucide-react";
import AnonymityBanner from "@/components/AnonymityBanner";
import NextStepsFooter from "@/components/NextStepsFooter";
import TransitionReassurance from "@/components/TransitionReassurance";
import "@/styles/govtech.css";

// Question Flow for Right to Buy Triage
const QUESTIONS = [
  {
    id: "tenancy_start_year",
    step: 1,
    question: "When did your social housing tenancy begin?",
    type: "select",
    options: [
      { value: "2024", label: "2024 or later" },
      { value: "2023", label: "2023" },
      { value: "2022", label: "2022" },
      { value: "2021", label: "2021" },
      { value: "2020", label: "2020" },
      { value: "2019", label: "2019" },
      { value: "2018", label: "2018" },
      { value: "2015", label: "2015 - 2017" },
      { value: "2010", label: "2010 - 2014" },
      { value: "2005", label: "2005 - 2009" },
      { value: "2000", label: "2000 - 2004" },
      { value: "1995", label: "1995 - 1999" },
      { value: "1990", label: "Before 1995" }
    ],
    insight: {
      type: "pro-tip",
      title: "10-Year Insight",
      content: "Your qualifying period includes ALL time as a public sector tenant—even if you've moved between councils or housing associations. Keep records of previous tenancies!"
    }
  },
  {
    id: "property_type",
    step: 2,
    question: "What type of property do you live in?",
    type: "select",
    options: [
      { value: "house", label: "House (including semi-detached, terraced)" },
      { value: "flat", label: "Flat or Maisonette" },
      { value: "bungalow", label: "Bungalow" }
    ],
    insight: {
      type: "pro-tip",
      title: "10-Year Insight",
      content: "Flats get a higher starting discount (50%) but houses build equity faster in most areas. Consider long-term value, not just the discount percentage."
    }
  },
  {
    id: "location",
    step: 3,
    question: "Where is your property located?",
    type: "select",
    options: [
      { value: "london", label: "London Borough" },
      { value: "outside_london", label: "Outside London (England)" }
    ],
    insight: null
  },
  {
    id: "joint_tenancy",
    step: 4,
    question: "Is this a joint tenancy?",
    type: "select",
    options: [
      { value: "sole", label: "No, I am the sole tenant" },
      { value: "joint_spouse", label: "Yes, joint with spouse/partner" },
      { value: "joint_family", label: "Yes, joint with family member(s)" }
    ],
    insight: {
      type: "pro-tip",
      title: "10-Year Insight",
      content: "Joint tenants can combine qualifying years. If your partner has 5 years and you have 3, you may qualify using the longest period."
    }
  },
  {
    id: "arrears_status",
    step: 5,
    question: "Is your rent account currently clear?",
    type: "select",
    options: [
      { value: "clear", label: "Yes, fully up to date" },
      { value: "minor", label: "Small arrears (less than 4 weeks)" },
      { value: "arrears", label: "Significant arrears" }
    ],
    insight: null
  },
  {
    id: "mortgage_ready",
    step: 6,
    question: "Have you explored mortgage options?",
    type: "select",
    options: [
      { value: "yes_aip", label: "Yes, I have an Agreement in Principle" },
      { value: "researching", label: "I'm researching but haven't applied" },
      { value: "no", label: "No, I haven't started yet" },
      { value: "cash", label: "I plan to purchase with cash/savings" }
    ],
    insight: {
      type: "pro-tip",
      title: "10-Year Insight",
      content: "Get a mortgage Agreement in Principle (AIP) before applying for RTB. It shows the Council you're a serious buyer and speeds up the process significantly."
    }
  }
];

// RTB Discount Calculation Logic
const calculateRTBDiscount = (answers) => {
  const currentYear = new Date().getFullYear();
  const startYear = parseInt(answers.tenancy_start_year) || currentYear;
  const yearsAssTenant = currentYear - startYear;
  
  const isFlat = answers.property_type === "flat";
  const isLondon = answers.location === "london";
  
  // Discount calculation based on UK RTB rules
  let discountPercent = 0;
  let eligible = false;
  let yearsUntilEligible = 0;
  
  if (yearsAssTenant >= 3) {
    eligible = true;
    if (isFlat) {
      // Flats: 50% base + 2% per year after 3 years, max 70%
      discountPercent = Math.min(50 + ((yearsAssTenant - 3) * 2), 70);
    } else {
      // Houses: 35% base + 1% per year after 3 years, max 70%
      discountPercent = Math.min(35 + ((yearsAssTenant - 3) * 1), 70);
    }
  } else {
    yearsUntilEligible = 3 - yearsAssTenant;
  }
  
  // Maximum discount caps (2024/25 figures)
  const maxDiscountCap = isLondon ? 96000 : 127900;
  
  // Estimate property value for illustration (UK social housing average)
  const estimatedPropertyValue = isLondon 
    ? (isFlat ? 350000 : 450000)
    : (isFlat ? 150000 : 220000);
  
  const calculatedDiscount = Math.round(estimatedPropertyValue * (discountPercent / 100));
  const actualDiscount = Math.min(calculatedDiscount, maxDiscountCap);
  const estimatedPurchasePrice = estimatedPropertyValue - actualDiscount;
  
  // Determine status
  let status, title, message;
  
  if (!eligible) {
    status = "amber";
    title = `A Little Longer: ${yearsUntilEligible} Year(s) to Go`;
    message = `Under UK housing rules, you usually need 3 years as a public sector tenant before Right to Buy becomes available. The great news is you can start preparing now — getting your finances in order and researching mortgage options early makes everything easier when the time comes.`;
  } else if (answers.arrears_status === "arrears") {
    status = "amber";
    title = "Address Rent Arrears Before Applying";
    message = `You qualify for a ${discountPercent}% discount, but significant rent arrears may delay or block your application. Clear your account first.`;
  } else if (discountPercent >= 60) {
    status = "green";
    title = `Maximum Discount Approaching: ${discountPercent}%`;
    message = `Excellent! You've built substantial discount. At ${yearsAssTenant} years, you're close to or at the 70% maximum. Consider acting soon.`;
  } else {
    status = "green";
    title = `Current Discount: ${discountPercent}%`;
    message = `You qualify for Right to Buy with a ${discountPercent}% discount. ${isFlat ? 'Your discount increases by 2%' : 'Your discount increases by 1%'} each additional year (up to 70% max).`;
  }
  
  // Years to max discount
  let yearsToMaxDiscount = 0;
  if (eligible && discountPercent < 70) {
    const annualIncrease = isFlat ? 2 : 1;
    yearsToMaxDiscount = Math.ceil((70 - discountPercent) / annualIncrease);
  }
  
  return {
    status,
    title,
    message,
    eligible,
    yearsAssTenant,
    yearsUntilEligible,
    discountPercent,
    maxDiscountCap,
    estimatedPropertyValue,
    calculatedDiscount,
    actualDiscount,
    estimatedPurchasePrice,
    isFlat,
    isLondon,
    yearsToMaxDiscount
  };
};

// Generate Comprehensive RTB PDF
const generatePDF = (answers, result) => {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const margin = 20;
  const contentWidth = pageWidth - (margin * 2);
  let yPos = 20;
  
  const addWrappedText = (text, x, y, maxWidth, lineHeight = 7) => {
    const lines = doc.splitTextToSize(text, maxWidth);
    doc.text(lines, x, y);
    return y + (lines.length * lineHeight);
  };
  
  // Header
  doc.setFillColor(0, 112, 60);
  doc.rect(0, 0, pageWidth, 40, 'F');
  
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(24);
  doc.setFont("helvetica", "bold");
  doc.text("TenURes.co.uk", margin, 25);
  
  doc.setFontSize(10);
  doc.setFont("helvetica", "normal");
  doc.text("RIGHT TO BUY GUIDANCE SUMMARY", margin, 35);
  
  yPos = 55;
  
  // Authority Stamp
  doc.setFillColor(244, 244, 244);
  doc.rect(margin, yPos, contentWidth, 25, 'F');
  doc.setDrawColor(0, 112, 60);
  doc.setLineWidth(1);
  doc.line(margin, yPos, margin, yPos + 25);
  
  doc.setTextColor(51, 51, 51);
  doc.setFontSize(9);
  doc.setFont("helvetica", "bold");
  doc.text("OFFICER-READY EVIDENCE PACK", margin + 5, yPos + 10);
  doc.setFont("helvetica", "normal");
  doc.text("Verified with 10 Years of Frontline Housing Management Experience", margin + 5, yPos + 18);
  
  yPos += 35;
  
  // Eligibility Status
  const statusColors = {
    green: { bg: [204, 226, 216], text: [0, 112, 60] },
    amber: { bg: [252, 214, 195], text: [110, 54, 25] }
  };
  
  const statusStyle = statusColors[result.status];
  doc.setFillColor(...statusStyle.bg);
  doc.rect(margin, yPos, contentWidth, 15, 'F');
  doc.setDrawColor(...statusStyle.text);
  doc.setLineWidth(1);
  doc.line(margin, yPos, margin, yPos + 15);
  
  doc.setTextColor(...statusStyle.text);
  doc.setFontSize(11);
  doc.setFont("helvetica", "bold");
  const statusLabel = result.eligible ? `ELIGIBLE - ${result.discountPercent}% DISCOUNT` : "NOT YET ELIGIBLE";
  doc.text(statusLabel, margin + 5, yPos + 10);
  
  yPos += 25;
  
  // Result
  doc.setTextColor(51, 51, 51);
  doc.setFontSize(16);
  doc.setFont("helvetica", "bold");
  yPos = addWrappedText(result.title, margin, yPos, contentWidth, 8);
  
  yPos += 5;
  doc.setFontSize(11);
  doc.setFont("helvetica", "normal");
  yPos = addWrappedText(result.message, margin, yPos, contentWidth);
  
  yPos += 15;
  
  // Discount Breakdown
  doc.setFontSize(14);
  doc.setFont("helvetica", "bold");
  doc.text("Your Discount Calculation", margin, yPos);
  yPos += 10;
  
  doc.setFillColor(240, 253, 244);
  doc.rect(margin, yPos, contentWidth, 60, 'F');
  
  doc.setFontSize(10);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(51, 51, 51);
  
  doc.text(`Qualifying Years: ${result.yearsAssTenant} years`, margin + 5, yPos + 10);
  doc.text(`Property Type: ${result.isFlat ? 'Flat/Maisonette' : 'House/Bungalow'}`, margin + 5, yPos + 18);
  doc.text(`Location: ${result.isLondon ? 'London' : 'Outside London'}`, margin + 5, yPos + 26);
  doc.text(`Discount Rate: ${result.discountPercent}%`, margin + 5, yPos + 34);
  doc.text(`Maximum Discount Cap: £${result.maxDiscountCap.toLocaleString()}`, margin + 5, yPos + 42);
  
  doc.setFont("helvetica", "bold");
  doc.setTextColor(0, 112, 60);
  doc.text(`YOUR DISCOUNT VALUE: £${result.actualDiscount.toLocaleString()}`, margin + 5, yPos + 54);
  
  yPos += 70;
  
  // Illustrative Purchase Price
  doc.setTextColor(51, 51, 51);
  doc.setFontSize(12);
  doc.setFont("helvetica", "bold");
  doc.text("Illustrative Purchase Calculation*", margin, yPos);
  yPos += 8;
  
  doc.setFontSize(10);
  doc.setFont("helvetica", "normal");
  doc.text(`Estimated Property Value: £${result.estimatedPropertyValue.toLocaleString()}`, margin, yPos);
  yPos += 7;
  doc.text(`Less RTB Discount (${result.discountPercent}%): -£${result.actualDiscount.toLocaleString()}`, margin, yPos);
  yPos += 7;
  doc.setFont("helvetica", "bold");
  doc.text(`Estimated Purchase Price: £${result.estimatedPurchasePrice.toLocaleString()}`, margin, yPos);
  yPos += 10;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.text("* Based on average values. Your actual valuation will be determined by an independent surveyor.", margin, yPos);
  
  yPos += 15;
  
  // Discount Growth Projection
  if (result.eligible && result.yearsToMaxDiscount > 0) {
    doc.setFontSize(12);
    doc.setFont("helvetica", "bold");
    doc.text("Discount Growth Projection", margin, yPos);
    yPos += 8;
    
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    const annualIncrease = result.isFlat ? 2 : 1;
    doc.text(`Current: ${result.discountPercent}%`, margin, yPos);
    yPos += 6;
    doc.text(`+1 Year: ${Math.min(result.discountPercent + annualIncrease, 70)}%`, margin, yPos);
    yPos += 6;
    doc.text(`+3 Years: ${Math.min(result.discountPercent + (annualIncrease * 3), 70)}%`, margin, yPos);
    yPos += 6;
    doc.text(`Maximum (70%): In ${result.yearsToMaxDiscount} more year(s)`, margin, yPos);
    yPos += 12;
  }
  
  // New page for Checklist
  doc.addPage();
  yPos = 20;
  
  doc.setFontSize(16);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(51, 51, 51);
  doc.text("Mortgage Readiness Checklist", margin, yPos);
  yPos += 15;
  
  doc.setFontSize(10);
  doc.setFont("helvetica", "normal");
  
  const mortgageChecklist = [
    "Check your credit score (Experian, Equifax, or TransUnion)",
    "Calculate your deposit amount (usually 5-10% of purchase price)",
    "Gather 3 months of payslips or proof of income",
    "Collect 3-6 months of bank statements",
    "List all existing debts and monthly outgoings",
    "Get a mortgage Agreement in Principle (AIP) from a lender",
    "Research RTB-specific mortgage products (some lenders specialise)",
    "Consider a mortgage broker experienced with RTB purchases",
    "Budget for survey, legal fees, and stamp duty (if applicable)"
  ];
  
  mortgageChecklist.forEach((item, index) => {
    doc.rect(margin, yPos - 3, 4, 4);
    doc.text(`${index + 1}. ${item}`, margin + 8, yPos);
    yPos += 10;
  });
  
  yPos += 10;
  
  // RTB Application Process
  doc.setFontSize(14);
  doc.setFont("helvetica", "bold");
  doc.text("RTB Application Process", margin, yPos);
  yPos += 12;
  
  doc.setFontSize(10);
  doc.setFont("helvetica", "normal");
  
  const processSteps = [
    "1. Submit RTB1 form to your landlord (they must respond within 4 weeks)",
    "2. Landlord issues RTB2 (admitting or denying your right)",
    "3. If admitted, landlord has 8 weeks (houses) or 12 weeks (flats) to send offer",
    "4. You have 12 weeks to accept the offer",
    "5. Arrange mortgage, solicitor, and independent valuation",
    "6. Complete the purchase"
  ];
  
  processSteps.forEach(step => {
    yPos = addWrappedText(step, margin, yPos, contentWidth, 7);
    yPos += 3;
  });
  
  yPos += 10;
  
  // Cover Letter
  doc.setFontSize(14);
  doc.setFont("helvetica", "bold");
  doc.text("Template Letter to Housing Officer", margin, yPos);
  yPos += 12;
  
  doc.setFontSize(10);
  doc.setFont("helvetica", "normal");
  
  const letterContent = `Dear Housing Officer,

RE: Right to Buy Enquiry

I am writing to express my interest in purchasing my property under the Right to Buy scheme.

My details:
- Tenancy start: ${answers.tenancy_start_year}
- Qualifying years: ${result.yearsAssTenant} years
- Property type: ${result.isFlat ? 'Flat' : 'House'}
- Estimated discount: ${result.discountPercent}%

I confirm that:
- This property is my only or principal home
- I am a secure tenant
- My rent account is ${answers.arrears_status === 'clear' ? 'clear' : 'being addressed'}

Please send me the RTB1 application form and confirm the next steps.

Yours faithfully,

[Your Name]
[Your Address]`;

  const letterLines = doc.splitTextToSize(letterContent, contentWidth);
  doc.text(letterLines, margin, yPos);
  
  // Footer
  const pageCount = doc.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(128, 128, 128);
    doc.text(
      `Generated by TenURes.co.uk | ${new Date().toLocaleDateString('en-GB')} | Page ${i} of ${pageCount}`,
      pageWidth / 2,
      doc.internal.pageSize.getHeight() - 10,
      { align: 'center' }
    );
    doc.text(
      "This is a guidance document. Valuations and eligibility are confirmed by your landlord.",
      pageWidth / 2,
      doc.internal.pageSize.getHeight() - 5,
      { align: 'center' }
    );
  }
  
  doc.save("TenURes_RTB_Guidance_Summary.pdf");
};

// Components
const TenuresLogo = () => (
  <span className="text-xl font-bold text-[#333333]">
    Ten<span className="text-[#00703C] font-extrabold">UR</span>es<span className="text-[#00703C]">.co.uk</span>
  </span>
);

const StatusBadge = ({ status, eligible, discount }) => {
  const config = {
    green: { icon: CheckCircle, className: "status-green" },
    amber: { icon: AlertTriangle, className: "status-amber" }
  };
  
  const { icon: Icon, className } = config[status];
  const label = eligible ? `${discount}% Discount` : "Not Quite Yet — Let's Prepare";
  
  return (
    <div className={`status-badge ${className}`}>
      <Icon size={18} />
      <span>{label}</span>
    </div>
  );
};

const DiscountMeter = ({ percent, maxPercent = 70 }) => {
  const fillWidth = (percent / maxPercent) * 100;
  
  return (
    <div className="mb-6">
      <div className="flex justify-between text-sm mb-2">
        <span className="text-gray-600">Your Discount</span>
        <span className="font-bold text-[#00703C]">{percent}%</span>
      </div>
      <div className="h-4 bg-gray-200 rounded-full overflow-hidden">
        <div 
          className="h-full bg-[#00703C] rounded-full transition-all duration-500"
          style={{ width: `${fillWidth}%` }}
        />
      </div>
      <div className="flex justify-between text-xs text-gray-500 mt-1">
        <span>0%</span>
        <span>Max 70%</span>
      </div>
    </div>
  );
};

const StatCard = ({ icon: Icon, label, value, sublabel }) => (
  <div className="bg-white border border-gray-200 p-4 text-center">
    <Icon className="text-[#00703C] mx-auto mb-2" size={24} />
    <p className="text-xs text-gray-500 mb-1">{label}</p>
    <p className="text-xl font-bold text-[#333333]">{value}</p>
    {sublabel && <p className="text-xs text-gray-400">{sublabel}</p>}
  </div>
);

export default function RTBTriage() {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [showMortgageInterest, setShowMortgageInterest] = useState(false);
  const [mortgageSubmitted, setMortgageSubmitted] = useState(false);
  const [mortgageForm, setMortgageForm] = useState({
    name: "",
    phone: "",
    email: "",
    bestTime: "anytime"
  });
  
  // Email capture state
  const [emailCapture, setEmailCapture] = useState({ email: "", postcode: "" });
  const [emailSent, setEmailSent] = useState(false);
  const [showEmailForm, setShowEmailForm] = useState(false);
  
  // Feedback state
  const [feedbackGiven, setFeedbackGiven] = useState(false);
  const [pdfDownloaded, setPdfDownloaded] = useState(false);
  
  const currentQuestion = QUESTIONS[currentStep];
  const progress = ((currentStep + 1) / QUESTIONS.length) * 100;
  
  const handleAnswer = (value) => {
    setAnswers({ ...answers, [currentQuestion.id]: value });
  };
  
  const handleNext = () => {
    if (!answers[currentQuestion.id]) return;
    
    if (currentStep < QUESTIONS.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      const triageResult = calculateRTBDiscount(answers);
      setResult(triageResult);
    }
  };
  
  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };
  
  const handleDownloadPDF = () => {
    generatePDF(answers, result);
    setPdfDownloaded(true);
  };
  
  const handleEmailSubmit = (e) => {
    e.preventDefault();
    if (!emailCapture.email) return;
    
    const leadData = {
      email: emailCapture.email,
      postcode: emailCapture.postcode,
      tool: "rtb",
      discount: result?.discount,
      timestamp: new Date().toISOString()
    };
    
    const existingLeads = JSON.parse(localStorage.getItem('tenures_resident_leads') || '[]');
    existingLeads.push(leadData);
    localStorage.setItem('tenures_resident_leads', JSON.stringify(existingLeads));
    
    console.log(`[Lead Captured] RTB Triage:`, leadData);
    setEmailSent(true);
    generatePDF(answers, result);
    setPdfDownloaded(true);
  };
  
  const handleFeedback = (isPositive) => {
    const feedbackData = {
      tool: "rtb",
      helpful: isPositive,
      discount: result?.discount,
      timestamp: new Date().toISOString()
    };
    
    const existingFeedback = JSON.parse(localStorage.getItem('tenures_feedback') || '[]');
    existingFeedback.push(feedbackData);
    localStorage.setItem('tenures_feedback', JSON.stringify(existingFeedback));
    
    console.log(`[Feedback] RTB Triage:`, feedbackData);
    setFeedbackGiven(true);
  };
  
  const handleRestart = () => {
    setCurrentStep(0);
    setAnswers({});
    setResult(null);
  };
  
  const handleShare = () => {
    const shareText = "I just calculated my Right to Buy discount on TenURes - it's free and takes 2 minutes. Find out how much you could save:";
    const shareUrl = window.location.origin + "/rtb-triage";
    
    if (navigator.share) {
      navigator.share({
        title: 'TenURes - Right to Buy Calculator',
        text: shareText,
        url: shareUrl
      });
    } else {
      window.open(`https://wa.me/?text=${encodeURIComponent(shareText + " " + shareUrl)}`, '_blank');
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="border-b border-gray-200 bg-white">
        <div className="max-w-4xl mx-auto px-4 py-4 flex justify-between items-center">
          <button onClick={() => navigate('/resident')} className="flex items-center gap-2 text-gray-600 hover:text-gray-900">
            <ArrowLeft size={20} />
            <span>Back to Portal</span>
          </button>
          <TenuresLogo />
        </div>
      </nav>
      
      {/* Progress Bar */}
      {!result && (
        <div className="max-w-4xl mx-auto px-4 pt-8">
          <div className="flex justify-between items-center mb-2">
            <span className="text-sm text-gray-500">Right to Buy Guidance Checker</span>
            <span className="text-sm text-gray-500">Step {currentStep + 1} of {QUESTIONS.length}</span>
          </div>
          <div className="progress-container">
            <div className="progress-fill" style={{ width: `${progress}%` }} />
          </div>
          <p className="text-sm text-gray-500 text-center">About 2 minutes to complete</p>
          <div className="mt-4">
            <AnonymityBanner />
          </div>
        </div>
      )}
      
      {/* Main Content */}
      <main className="max-w-2xl mx-auto px-4 py-12">
        {/* Authority Stamp */}
        <div className="authority-stamp mb-8">
          <div className="authority-stamp-icon">
            <Shield size={20} />
          </div>
          <div>
            <p className="font-semibold text-gray-900">Designed with a Decade of Frontline Housing Expertise</p>
            <p className="text-sm text-gray-600">Accurate RTB discount calculations based on current legislation</p>
          </div>
        </div>
        
        {!result ? (
          /* Question View */
          <div className="govtech-card">
            <TransitionReassurance step={currentStep} />
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-full bg-[#00703C] bg-opacity-10 flex items-center justify-center">
                <Key className="text-[#00703C]" size={20} />
              </div>
              <h1 className="govtech-heading-m">{currentQuestion.question}</h1>
            </div>
            
            {/* Pro Tip */}
            {currentQuestion.insight && (
              <div className="pro-tip mb-6">
                <div className="flex items-center gap-2 mb-1">
                  <Lightbulb size={16} className="text-[#F47738]" />
                  <span className="pro-tip-label">{currentQuestion.insight.title}</span>
                </div>
                <p className="text-gray-700 text-sm">{currentQuestion.insight.content}</p>
              </div>
            )}
            
            {/* Options */}
            <div className="govtech-radio-group mb-8">
              {currentQuestion.options.map((option) => (
                <div
                  key={option.value}
                  onClick={() => handleAnswer(option.value)}
                  className={`govtech-radio-item ${answers[currentQuestion.id] === option.value ? 'selected' : ''}`}
                  data-testid={`option-${option.value}`}
                >
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                    answers[currentQuestion.id] === option.value 
                      ? 'border-[#00703C] bg-[#00703C]' 
                      : 'border-gray-400'
                  }`}>
                    {answers[currentQuestion.id] === option.value && (
                      <div className="w-2 h-2 rounded-full bg-white" />
                    )}
                  </div>
                  <span className="text-gray-900">{option.label}</span>
                </div>
              ))}
            </div>
            
            {/* Navigation Buttons */}
            <div className="flex gap-4">
              {currentStep > 0 && (
                <button
                  onClick={handleBack}
                  className="govtech-btn govtech-btn-secondary flex-1"
                >
                  <ArrowLeft size={18} className="mr-2" />
                  Back
                </button>
              )}
              <button
                onClick={handleNext}
                disabled={!answers[currentQuestion.id]}
                className={`govtech-btn govtech-btn-primary flex-1 ${!answers[currentQuestion.id] ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                {currentStep < QUESTIONS.length - 1 ? (
                  <>
                    Continue
                    <ArrowRight size={18} className="ml-2" />
                  </>
                ) : (
                  "Calculate My Discount"
                )}
              </button>
            </div>
          </div>
        ) : (
          /* Result View */
          <div className="space-y-6">
            <div className="govtech-card">
              <AnonymityBanner variant="full" />
              <div className="text-center mb-6">
                <h1 className="govtech-heading-m mb-4">Your Right to Buy Guidance Summary</h1>
                <StatusBadge status={result.status} eligible={result.eligible} discount={result.discountPercent} />
              </div>

              <NextStepsFooter reviewDays={28} teamName="Right to Buy team" />
              
              {/* Discount Meter */}
              {result.eligible && (
                <DiscountMeter percent={result.discountPercent} />
              )}
              
              <div className="govtech-card govtech-card-highlight mb-6">
                <h2 className="font-bold text-gray-900 mb-2">{result.title}</h2>
                <p className="text-gray-700">{result.message}</p>
              </div>
              
              {/* Key Stats */}
              <div className="grid grid-cols-3 gap-4 mb-6">
                <StatCard 
                  icon={Calendar}
                  label="Qualifying Years"
                  value={result.yearsAssTenant}
                  sublabel="years"
                />
                <StatCard 
                  icon={TrendingUp}
                  label="Your Discount"
                  value={`${result.discountPercent}%`}
                  sublabel={result.isFlat ? "+2%/year" : "+1%/year"}
                />
                <StatCard 
                  icon={PoundSterling}
                  label="Discount Value"
                  value={`£${(result.actualDiscount / 1000).toFixed(0)}k`}
                  sublabel="estimated"
                />
              </div>
              
              {/* Purchase Illustration */}
              {result.eligible && (
                <div className="bg-[#F8F8F8] p-4 mb-6">
                  <h3 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                    <Calculator className="text-[#00703C]" size={20} />
                    Illustrative Purchase Price
                  </h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Estimated Property Value</span>
                      <span className="text-gray-900">£{result.estimatedPropertyValue.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between text-[#00703C]">
                      <span>RTB Discount ({result.discountPercent}%)</span>
                      <span>-£{result.actualDiscount.toLocaleString()}</span>
                    </div>
                    <div className="border-t border-gray-300 pt-2 flex justify-between font-bold">
                      <span>Your Purchase Price</span>
                      <span className="text-[#00703C]">£{result.estimatedPurchasePrice.toLocaleString()}</span>
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 mt-3">
                    * Illustration only. Actual value determined by independent valuation.
                  </p>
                </div>
              )}
              
              {/* Growth Projection */}
              {result.eligible && result.yearsToMaxDiscount > 0 && (
                <div className="bg-gray-50 p-4 mb-6">
                  <h3 className="font-bold text-gray-900 mb-2">Discount Growth</h3>
                  <p className="text-sm text-gray-700">
                    Your discount increases by {result.isFlat ? '2%' : '1%'} each year. 
                    You'll reach the maximum 70% in <strong>{result.yearsToMaxDiscount} more year(s)</strong>.
                  </p>
                </div>
              )}
              
              {/* Mortgage Partner CTA */}
              <div className="bg-[#00703C] bg-opacity-5 border border-[#00703C] border-opacity-30 p-6 mb-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full bg-[#00703C] bg-opacity-20 flex items-center justify-center flex-shrink-0">
                    <CreditCard className="text-[#00703C]" size={24} />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-bold text-[#333333] mb-2">Need Mortgage Guidance?</h3>
                    <p className="text-sm text-gray-700 mb-4">
                      We've partnered with specialist RTB mortgage advisors who understand social housing purchases. 
                      They can help you understand your borrowing options at no obligation.
                    </p>
                    <button 
                      onClick={() => setShowMortgageInterest(true)}
                      className="govtech-btn govtech-btn-primary text-sm px-6 py-2"
                      data-testid="mortgage-partner-btn"
                    >
                      Connect Me With a Mortgage Partner
                      <ArrowRight size={16} className="ml-2" />
                    </button>
                  </div>
                </div>
              </div>

              {/* Mortgage Interest Modal */}
              {showMortgageInterest && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                  <div className="bg-white max-w-md w-full p-6 shadow-xl">
                    {!mortgageSubmitted ? (
                      <>
                        <h3 className="text-xl font-bold text-[#333333] mb-2">Connect With Our Mortgage Partners</h3>
                        <p className="text-sm text-gray-600 mb-6">
                          Leave your details and our specialist RTB mortgage partner will reach out to discuss your options. 
                          This is a free, no-obligation service.
                        </p>
                        <div className="space-y-4">
                          <div>
                            <label className="block text-sm font-semibold text-[#333333] mb-1">Your Name *</label>
                            <input
                              type="text"
                              className="govtech-input"
                              value={mortgageForm.name}
                              onChange={(e) => setMortgageForm({...mortgageForm, name: e.target.value})}
                              placeholder="Full name"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-semibold text-[#333333] mb-1">Phone Number *</label>
                            <input
                              type="tel"
                              className="govtech-input"
                              value={mortgageForm.phone}
                              onChange={(e) => setMortgageForm({...mortgageForm, phone: e.target.value})}
                              placeholder="07xxx xxxxxx"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-semibold text-[#333333] mb-1">Email (Optional)</label>
                            <input
                              type="email"
                              className="govtech-input"
                              value={mortgageForm.email}
                              onChange={(e) => setMortgageForm({...mortgageForm, email: e.target.value})}
                              placeholder="your@email.com"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-semibold text-[#333333] mb-1">Best Time to Call</label>
                            <select
                              className="govtech-input"
                              value={mortgageForm.bestTime}
                              onChange={(e) => setMortgageForm({...mortgageForm, bestTime: e.target.value})}
                            >
                              <option value="anytime">Anytime</option>
                              <option value="morning">Morning (9am - 12pm)</option>
                              <option value="afternoon">Afternoon (12pm - 5pm)</option>
                              <option value="evening">Evening (5pm - 8pm)</option>
                            </select>
                          </div>
                        </div>
                        <div className="flex gap-3 mt-6">
                          <button
                            onClick={() => setShowMortgageInterest(false)}
                            className="govtech-btn govtech-btn-secondary flex-1"
                          >
                            Cancel
                          </button>
                          <button
                            onClick={() => {
                              console.log("[Mortgage Lead]", { ...mortgageForm, discount: result.discountPercent, estimatedPrice: result.estimatedPurchasePrice });
                              setMortgageSubmitted(true);
                            }}
                            disabled={!mortgageForm.name || !mortgageForm.phone}
                            className={`govtech-btn govtech-btn-primary flex-1 ${(!mortgageForm.name || !mortgageForm.phone) ? 'opacity-50' : ''}`}
                          >
                            Submit
                          </button>
                        </div>
                        <p className="text-xs text-gray-500 mt-4 text-center">
                          By submitting, you agree to be contacted by our mortgage partner.
                        </p>
                      </>
                    ) : (
                      <div className="text-center py-6">
                        <div className="w-16 h-16 rounded-full bg-[#00703C] flex items-center justify-center mx-auto mb-4">
                          <CheckCircle className="text-white" size={32} />
                        </div>
                        <h3 className="text-xl font-bold text-[#333333] mb-2">Thank You!</h3>
                        <p className="text-gray-600 mb-6">
                          Our mortgage partner will be in touch within 2 working days to discuss your RTB options.
                        </p>
                        <button
                          onClick={() => {
                            setShowMortgageInterest(false);
                            setMortgageSubmitted(false);
                          }}
                          className="govtech-btn govtech-btn-primary"
                        >
                          Close
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* What's in the PDF */}
              <div className="bg-gray-50 p-4 mb-6">
                <h3 className="font-bold text-gray-900 mb-3">Your Comprehensive Evidence Pack includes:</h3>
                <ul className="space-y-2 text-sm text-gray-700">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="text-[#00703C] mt-0.5" size={16} />
                    <span><strong>Discount Calculation</strong> — Full breakdown of your RTB entitlement</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="text-[#00703C] mt-0.5" size={16} />
                    <span><strong>Mortgage Readiness Checklist</strong> — Steps to prepare for purchase</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="text-[#00703C] mt-0.5" size={16} />
                    <span><strong>RTB Process Timeline</strong> — What to expect at each stage</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="text-[#00703C] mt-0.5" size={16} />
                    <span><strong>Cover Letter Template</strong> — Professional letter for your landlord</span>
                  </li>
                </ul>
              </div>
              
              {/* Action Buttons */}
              <div className="flex flex-col gap-3">
                {/* Email Capture Option */}
                {!showEmailForm && !emailSent && (
                  <button
                    onClick={() => setShowEmailForm(true)}
                    className="govtech-btn govtech-btn-primary w-full"
                    data-testid="email-pdf-btn"
                  >
                    <Mail size={18} className="mr-2" />
                    Email Me My Report
                  </button>
                )}
                
                {showEmailForm && !emailSent && (
                  <form onSubmit={handleEmailSubmit} className="bg-gray-50 p-4 space-y-3">
                    <p className="text-sm font-medium text-[#333333]">Get your report sent to your inbox:</p>
                    <input
                      type="email"
                      required
                      placeholder="your@email.com"
                      className="govtech-input"
                      value={emailCapture.email}
                      onChange={(e) => setEmailCapture({...emailCapture, email: e.target.value})}
                    />
                    <input
                      type="text"
                      placeholder="Your postcode (optional)"
                      className="govtech-input"
                      value={emailCapture.postcode}
                      onChange={(e) => setEmailCapture({...emailCapture, postcode: e.target.value})}
                    />
                    <div className="flex gap-2">
                      <button type="submit" className="govtech-btn govtech-btn-primary flex-1">
                        <Send size={16} className="mr-2" />
                        Send & Download
                      </button>
                      <button 
                        type="button" 
                        onClick={() => setShowEmailForm(false)}
                        className="govtech-btn govtech-btn-secondary"
                      >
                        Cancel
                      </button>
                    </div>
                    <p className="text-xs text-gray-500">We'll only email you your report. No spam, ever.</p>
                  </form>
                )}
                
                {emailSent && (
                  <div className="bg-[#00703C] bg-opacity-10 border border-[#00703C] p-4 text-center">
                    <CheckCircle className="text-[#00703C] mx-auto mb-2" size={24} />
                    <p className="text-[#00703C] font-medium">Report sent to {emailCapture.email}</p>
                  </div>
                )}
                
                {!showEmailForm && (
                  <button
                    onClick={handleDownloadPDF}
                    className="govtech-btn govtech-btn-secondary w-full"
                    data-testid="download-pdf-btn"
                  >
                    <Download size={18} className="mr-2" />
                    {emailSent ? 'Download Again' : 'Just Download PDF'}
                  </button>
                )}
                
                {/* Feedback Section */}
                {pdfDownloaded && !feedbackGiven && (
                  <div className="bg-gray-50 p-4 mt-2">
                    <p className="text-sm font-medium text-[#333333] text-center mb-3">Was this tool helpful?</p>
                    <div className="flex justify-center gap-4">
                      <button
                        onClick={() => handleFeedback(true)}
                        className="flex items-center gap-2 px-6 py-2 bg-white border border-gray-200 hover:border-[#00703C] hover:bg-[#00703C] hover:bg-opacity-5 transition-all"
                      >
                        <ThumbsUp size={18} className="text-[#00703C]" />
                        <span>Yes</span>
                      </button>
                      <button
                        onClick={() => handleFeedback(false)}
                        className="flex items-center gap-2 px-6 py-2 bg-white border border-gray-200 hover:border-red-400 hover:bg-red-50 transition-all"
                      >
                        <ThumbsDown size={18} className="text-gray-500" />
                        <span>No</span>
                      </button>
                    </div>
                  </div>
                )}
                
                {feedbackGiven && (
                  <div className="bg-[#00703C] bg-opacity-5 p-3 text-center">
                    <p className="text-sm text-[#00703C]">Thank you for your feedback!</p>
                  </div>
                )}
                
                <button
                  onClick={handleShare}
                  className="govtech-btn govtech-btn-secondary w-full"
                  data-testid="share-btn"
                >
                  <Share2 size={18} className="mr-2" />
                  Share With a Neighbour
                </button>
                
                <button
                  onClick={handleRestart}
                  className="text-[#00703C] font-medium text-sm hover:underline text-center"
                >
                  Start a New Guidance Checker
                </button>
              </div>
            </div>
          </div>
        )}
      </main>
      
      {/* Footer */}
      <footer className="border-t border-gray-200 py-8 mt-12">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <p className="text-sm text-gray-500">
            This Guidance Checker is here to help you understand your options.
            Final valuations and your exact entitlement are confirmed by your landlord.
          </p>
          <p className="text-sm text-gray-400 mt-2">
            © {new Date().getFullYear()} Ten<span className="text-[#00703C] font-semibold">UR</span>es.co.uk — Professional Housing Infrastructure
          </p>
        </div>
      </footer>
    </div>
  );
}
